import * as Sentry from '@sentry/react'

// Answers that could not be saved because the connection failed (no response, timeout) or because
// the app is offline and the process is not on the device. They are kept here (survives a reload)
// and sent again until the server confirms them. Before this, such answers looked saved on screen
// but were lost (roadmap 03-offline-v2, findings 7 and 8). Offline v2 replaces this with an outbox.

const STORAGE_KEY = 'alphaprocess.pendingResults.v1'
const REJECTED_KEY = 'alphaprocess.rejectedResults.v1'

export type PendingKind = 'create' | 'update'

export interface PendingResult {
  key: string
  kind: PendingKind
  checklistId: number
  resultSetId: number
  accessToken?: string | null
  // the user who answered (null for shared links without an account)
  userId?: number | null
  payload: Record<string, unknown>
  queuedAt: number
  attempts: number
}

export type SendOutcome = 'ok' | 'network' | 'rejected'

type Listener = (count: number) => void
const listeners = new Set<Listener>()

function read(key: string): PendingResult[] {
  try {
    const raw = window.localStorage.getItem(key)
    const list = raw ? JSON.parse(raw) : []
    return Array.isArray(list) ? list : []
  } catch {
    return []
  }
}

function write(key: string, list: PendingResult[]) {
  try {
    window.localStorage.setItem(key, JSON.stringify(list))
  } catch (error) {
    // storage full or blocked: the answer stays in memory for this page and Sentry gets told
    Sentry.captureException(error, { tags: { area: 'pending-results' } })
  }
}

let memory: PendingResult[] | null = null

function all(): PendingResult[] {
  if (memory === null) memory = read(STORAGE_KEY)
  return memory
}

function save(list: PendingResult[]) {
  memory = list
  write(STORAGE_KEY, list)
  listeners.forEach((listener) => listener(list.length))
}

// the same answer (by id, or for a new answer by question and position) keeps only its newest value
export function pendingKey(kind: PendingKind, resultSetId: number, payload: Record<string, unknown>): string {
  if (kind === 'update' && payload.id) return `u:${resultSetId}:${payload.id}`
  return `c:${resultSetId}:${payload.question_id}:${payload.area_position ?? 1}:${payload.question_position ?? 1}`
}

export function queuePendingResult(entry: Omit<PendingResult, 'key' | 'queuedAt' | 'attempts'>): void {
  const key = pendingKey(entry.kind, entry.resultSetId, entry.payload)
  const list = all().filter((x) => x.key !== key)
  save([...list, {
    ...entry, key, queuedAt: Date.now(), attempts: 0
  }])
  Sentry.addBreadcrumb({ category: 'pending-results', message: `queued ${entry.kind} for result set ${entry.resultSetId}`, level: 'warning' })
}

export function pendingResultsCount(): number {
  return all().length
}

// answers on their way to the server right now (sent, no response yet); for the save status and
// so that closing a process waits for them
let savingCount = 0
const savingListeners = new Set<Listener>()

export function answersBeingSaved(): number {
  return savingCount
}

export function subscribeAnswersBeingSaved(listener: Listener): () => void {
  savingListeners.add(listener)
  return () => { savingListeners.delete(listener) }
}

export async function trackSaving<T>(request: Promise<T>): Promise<T> {
  savingCount++
  savingListeners.forEach((l) => l(savingCount))
  try {
    return await request
  } finally {
    savingCount--
    savingListeners.forEach((l) => l(savingCount))
  }
}

export function subscribePendingResults(listener: Listener): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

let flushing: Promise<{ sent: number, rejected: number }> | null = null

// Sends the queue in order. Stops at the first network failure (still offline). An answer the
// server rejects (e.g. the process was closed meanwhile) is moved aside and reported, not retried
// forever and not silently deleted.
export function flushPendingResults(
  send: (entry: PendingResult) => Promise<SendOutcome>,
  currentUserId: number | null
): Promise<{ sent: number, rejected: number }> {
  if (flushing) return flushing
  flushing = (async () => {
    let sent = 0
    let rejected = 0
    const queue = [...all()].sort((a, b) => a.queuedAt - b.queuedAt)
    for (const entry of queue) {
      // answers of another account on this device wait until that account is back
      if (!entry.accessToken && entry.userId && currentUserId && entry.userId !== currentUserId) continue
      if (!entry.accessToken && !currentUserId) continue

      let outcome: SendOutcome
      try {
        outcome = await send(entry)
      } catch {
        outcome = 'network'
      }
      if (outcome === 'network') {
        save(all().map((x) => (x.key === entry.key ? { ...x, attempts: x.attempts + 1 } : x)))
        break
      }
      // a newer value for the same answer may have been queued while this one was on its way
      const current = all().find((x) => x.key === entry.key)
      const replaced = current && current.queuedAt !== entry.queuedAt
      if (!replaced) save(all().filter((x) => x.key !== entry.key))
      if (outcome === 'ok') {
        sent += 1
      } else {
        rejected += 1
        write(REJECTED_KEY, [...read(REJECTED_KEY), entry].slice(-200))
        Sentry.captureMessage('Pending answer rejected by the server', {
          level: 'error',
          tags: { area: 'pending-results' },
          extra: { resultSetId: entry.resultSetId, kind: entry.kind, key: entry.key, attempts: entry.attempts }
        })
      }
    }
    return { sent, rejected }
  })().finally(() => {
    flushing = null
  })
  return flushing
}
